import { getAnimationClass } from '@/components/common/toast/utils'
import { Button } from '@/components/ui/button'
import { TagThumbnail } from '@/components/ui/tag-thumbnail'
import { sanitize } from '@/lib/helpers/sanitize'
import { X } from 'lucide-react'
import { toast as toastLib } from 'sonner'
import { ImageCM } from '../../extension/image-cm'

type ToastCmMessageParams = {
  position?:
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right'
  duration?: number
  title: string
  message?: string
  dangerouslyMessage?: string
  imageUrl?: string
  ctaButton?: {
    title: string
    onClick: () => void
  }
  ctaButtonSecond?: {
    title: string
    onClick: () => void
  }
  tag?: {
    title: string
    icon?: React.ReactNode
  }
}

export const showToastMessage = ({
  position = 'top-right',
  duration = 3000,
  title,
  message,
  dangerouslyMessage,
  imageUrl,
  ctaButton,
  ctaButtonSecond,
  tag,
}: ToastCmMessageParams) => {
  return toastLib.custom(
    (id) => (
      <div className='relative flex w-full items-start gap-2 border-l-4 border-l-neutral-900 px-6 py-4 pr-8'>
        <button
          aria-controls='close button'
          className='absolute right-1 top-1 flex h-6 w-6 items-center justify-center text-neutral-900 hover:text-neutral-600'
          onClick={() => toastLib.dismiss(id)}
        >
          <X size={16} />
        </button>
        {imageUrl && (
          <ImageCM
            src={imageUrl}
            width={92}
            height={123}
            useMediaUrl
            className='rounded-lg'
          />
        )}
        <div>
          {tag && (
            <TagThumbnail
              title={tag?.title}
              icon={tag?.icon}
              className='mb-1 normal-case'
              variant='secondary'
              size='small'
            />
          )}

          <p className='mb-1 font-criteria text-base font-semibold text-neutral-900'>
            {title}
          </p>
          {dangerouslyMessage ? (
            <div
              className='font-sans text-sm text-neutral-500'
              dangerouslySetInnerHTML={{
                __html: sanitize(dangerouslyMessage ?? ''),
              }}
            />
          ) : (
            <p className='whitespace-pre-wrap font-sans text-sm text-neutral-500'>
              {message}
            </p>
          )}

          {(ctaButton || ctaButtonSecond) && (
            <div className='mt-4'>
              {ctaButton && (
                <Button
                  size='small'
                  variant='black'
                  className='mr-1'
                  onClick={ctaButton.onClick}
                >
                  {ctaButton.title}
                </Button>
              )}

              {ctaButtonSecond && (
                <Button
                  variant='outline-neutral'
                  size='small'
                  onClick={ctaButtonSecond.onClick}
                >
                  {ctaButtonSecond.title}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    ),
    {
      duration,
      position,
      unstyled: true,
      className: `px-0 bg-light w-fit right-0 h-fit shadow-[0px_1px_4px_0px_hsla(0,0%,0%,0.06),_0px_0px_24px_0px_hsla(0,0%,0%,0.06)] overflow-hidden rounded-2xl border
        border-neutral-200 border-l-0 ${getAnimationClass(position)}`,
      cancel: true,
    }
  )
}
